import { last } from 'lodash';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

/**
 * Get view of an url.
 * @function getView
 * @param {string} url Url to be parsed.
 * @return {string} View of content object.
 */
export function getView(url) {
  const view = last(url.replace(/\?.*$/, '').split('/'));
  if (
    [
      'add',
      'layout',
      'contents',
      'edit',
      'delete',
      'diff',
      'history',
      'sharing',
      'controlpanel',
      'asideLeftSlot',
    ].indexOf(view) === -1
  ) {
    return 'view';
  }
  return view === 'layout' || 'asideLeftSlot' ? 'edit' : view;
}

export function removeUrlDownload(url) {
  let new_url = '';

  if (isInternalURL(url)) {
    new_url = url.includes('@@download')
      ? url.replace('/@@download/file', '')
      : url;
    new_url = url.includes('@@display-file')
      ? url.replace('/@@display-file/file', '')
      : url;
    return new_url;
  }
  return url;
}

/**
 * Origens por onde o portal é servido ao público (`apiPath`/`publicURL`).
 *
 * Serve para decidir se o id do site pode aparecer no caminho: URL na origem
 * pública já passou pelo VirtualHostRoot, então ali `/Plone` (ou o
 * `PloneSiteId` do ambiente) nunca é prefixo do site — é conteúdo de verdade.
 */
function getPublicOrigins() {
  const { settings } = config;

  return [settings?.apiPath, settings?.publicURL]
    .filter((base) => typeof base === 'string' && /^https?:\/\//i.test(base))
    .map((base) => {
      try {
        return new URL(base).origin;
      } catch {
        return '';
      }
    })
    .filter(Boolean);
}

/**
 * Prefixos de caminho do site Plone (`/Plone`) conhecidos pela configuração.
 * O mesmo backend é referenciado por chaves diferentes conforme o ambiente
 * (SSR, dev proxy, seamless), então juntamos todas e usamos a que casar.
 */
function getSitePrefixes() {
  const { settings } = config;

  return [
    settings?.internalApiPath,
    settings?.apiPath,
    settings?.publicURL,
    settings?.devProxyToApiPath,
  ]
    .filter((base) => typeof base === 'string' && base)
    .map((base) => {
      if (/^https?:\/\//i.test(base)) {
        try {
          return new URL(base).pathname;
        } catch {
          return '';
        }
      }
      return base.startsWith('/') ? base : '';
    })
    .map((prefix) => prefix.replace(/\/+$/, ''))
    .filter((prefix) => prefix && prefix !== '/');
}

/**
 * Converte uma URL vinda do backend no caminho interno equivalente do app.
 *
 * O Plone monta `Location` e `@id` com a origem que o Zope enxerga: o `Host`
 * do VHM, a porta real do processo e o id do site (`/Plone`). Nada disso
 * existe no roteador do Volto, e `flattenToAppURL` só remove
 * `internalApiPath`/`apiPath`/`publicURL` — qualquer outra origem passa
 * inteira e vira um `<Redirect>` para fora do portal.
 *
 * Aqui a origem é descartada de propósito: o destino é sempre interno, o que
 * de quebra impede um open redirect a partir de um `Location` do backend.
 *
 * @function toAppPath
 * @param {string} url URL absoluta ou caminho vindo do backend.
 * @return {?string} Caminho interno (sem querystring) ou `null` se não der
 * para confiar na entrada.
 */
export function toAppPath(url) {
  if (!url || typeof url !== 'string') return null;

  // URL protocol-relative (`//host/x`): sem origem confiável, não redireciona.
  if (url.startsWith('//')) return null;

  let path = url;
  // Só a URL vinda de uma origem interna (backend, VHM do proxy de dev) traz
  // o id do site no caminho; na origem pública o VirtualHostRoot já o removeu.
  let mayCarrySiteId = true;

  if (/^https?:\/\//i.test(url)) {
    let parsed;
    try {
      parsed = new URL(url);
    } catch {
      return null;
    }
    path = parsed.pathname;
    mayCarrySiteId = !getPublicOrigins().includes(parsed.origin);
  } else {
    path = url.replace(/\?.*$/, '');
  }

  const prefix = mayCarrySiteId
    ? getSitePrefixes()
        .filter((item) => path === item || path.startsWith(`${item}/`))
        .sort((a, b) => b.length - a.length)[0]
    : undefined;

  if (prefix) {
    path = path.slice(prefix.length);
  }

  path = path.replace('/++api++', '');

  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  return path === '/' ? '/' : path.replace(/\/+$/, '') || '/';
}
